import React, { useState, useEffect } from 'react';
import api from './services/api';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Menu, X } from 'lucide-react'; // Import Menu and X icons
import './App.css';
import logoPro from './logo_pro.png'; // Import logo for mobile header

// Components
import { Sidebar } from './components/Sidebar';
import { Background } from './components/Background';
import { LoginModal } from './LoginModal';
import { IDFinderModal } from './IDFinderModal';
import { ConfirmModal } from './ConfirmModal';
import { Toast } from './Toast';

// Pages
import { Dashboard } from './pages/Dashboard';
import { Bots } from './pages/Bots';
import { Repasse } from './pages/Repasse';
import { Plans } from './pages/Plans';
import { Groups } from './pages/Groups';
import { MediaLib } from './pages/MediaLib';
import { Checkout } from './pages/Checkout';
import { HotCloner } from './pages/HotCloner';
import { Financeiro } from './pages/Financeiro';
import { Assinar } from './pages/Assinar';
import { Networking } from './pages/Networking';
import { PublicCheckout } from './pages/PublicCheckout';
import { Admin } from './pages/Admin';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { AdminLogin } from './pages/AdminLogin';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminSidebar } from './components/AdminSidebar';
import { FreePlanBlocker } from './components/FreePlanBlocker';
import { WhatsAppButton } from './components/WhatsAppButton';
import { NoAccountBanner } from './components/NoAccountBanner';
import { useAuth } from './context/AuthContext';

// Admin Sub-pages
import { UsersList } from './pages/admin/UsersList';
import { UserDetail } from './pages/admin/UserDetail';
import { FeeConfig } from './pages/admin/FeeConfig';
import { BotsList } from './pages/admin/BotsList';
import { BotDetail } from './pages/admin/BotDetail';
import { WithdrawalsList } from './pages/admin/WithdrawalsList';
import { GatewaysList } from './pages/admin/GatewaysList';
import { SubscriptionsList } from './pages/admin/SubscriptionsList';
import { TransactionsList } from './pages/admin/TransactionsList';
import { WithdrawalBalance } from './pages/admin/WithdrawalBalance';
import { TelegramAppsList } from './pages/admin/TelegramAppsList';
import { PlatformSettings } from './pages/admin/PlatformSettings';
import { ActiveUsers } from './pages/admin/ActiveUsers';

interface Account {
  id: number | string;
  phone: string;
  is_active: boolean;
  status: string;
  user_id?: string | null;
}

function App() {
  const { user: authUser } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | string | null>(null);

  // UI State
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showIDFinderModal, setShowIDFinderModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean, title: string, msg: string, onConfirm: () => void } | null>(null);
  const [toast, setToast] = useState<{ msg: string, type: 'success' | 'info' | 'error' } | null>(null);

  // Mobile Sidebar State
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const fetchAccounts = async () => {
    const currentUserId = authUser?.id ?? null;
    try {
      const res = await api.get('/accounts');
      const raw = Array.isArray(res.data) ? res.data : [];
      const isAdmin = authUser?.role === 'admin' || authUser?.id === 'master-admin-uuid';
      const list = isAdmin
        ? raw
        : currentUserId
          ? raw.filter((a: Account) => a.user_id == null || String(a.user_id) === String(currentUserId))
          : [];
      setAccounts(list);

      const unlinked = list.filter((a: Account) => a.user_id == null);
      if (unlinked.length > 0) {
        await Promise.all(
          unlinked.map((a: Account) =>
            api.patch(`/accounts/${a.id}/link`).catch(() => {})
          )
        );
        const res2 = await api.get('/accounts');
        const raw2 = Array.isArray(res2.data) ? res2.data : [];
        const list2 = isAdmin
          ? raw2
          : currentUserId
            ? raw2.filter((a: Account) => a.user_id == null || String(a.user_id) === String(currentUserId))
            : [];
        setAccounts(list2);
      }
    } catch (error: unknown) {
      console.error("Erro ao buscar contas", error);
      setAccounts([]);
      setToast({ msg: 'Não foi possível carregar as contas.', type: 'error' });
    }
  };

  // Buscar contas ao montar e quando o usuário logado mudar (cada usuário vê só suas contas).
  // Não pular em /login: ao logar, authUser é setado antes do redirect, e precisamos buscar já.
  // Refetches em 400ms, 1s e 2.2s para o número aparecer na sidebar sem F5.
  useEffect(() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path.startsWith('/banana') || path === '/cadastro') return;
    if (!authUser?.id) {
      setAccounts([]);
      setSelectedAccountId(null);
      return;
    }
    fetchAccounts();
    const t1 = setTimeout(fetchAccounts, 400);
    const t2 = setTimeout(fetchAccounts, 1000);
    const t3 = setTimeout(fetchAccounts, 2200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [authUser?.id]);

  useEffect(() => {
    if (!selectedAccountId && accounts.length > 0) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId]);

  const linkAccountAndRefresh = async (accountId: string | number) => {
    try {
      await api.patch(`/accounts/${accountId}/link`);
      await fetchAccounts();
    } catch (e) {
      console.error("Erro ao vincular conta", e);
      fetchAccounts();
    }
  };

  const handleLoginModalSuccess = (accountId?: string | number) => {
    if (accountId) linkAccountAndRefresh(accountId);
    else fetchAccounts();
  };

  const handleDeleteAccountClick = (e: React.MouseEvent, id: number | string) => {
    e.stopPropagation();
    setConfirmModal({
      isOpen: true,
      title: "Desconectar Conta?",
      msg: "O bot irá parar de funcionar para esta conta. Tem certeza?",
      onConfirm: () => confirmDeleteAccount(id)
    });
  };

  const confirmDeleteAccount = async (id: number | string) => {
    try {
      await api.delete(`/accounts/${id}`);
      setAccounts(accounts.filter(acc => acc.id !== id));
      if (selectedAccountId === id) setSelectedAccountId(null);
      setToast({ msg: "Conta removida.", type: "success" });
    } catch (e) {
      setToast({ msg: "Erro ao remover conta.", type: "error" });
    } finally {
      setConfirmModal(null);
    }
  };

  // Identificar se estamos no domínio de Checkout (Público)
  const isPublicCheckout = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // Localhost: checkout.localhost
    if (hostname.includes('localhost')) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('preview') === 'true') return true;
      return parts.length > 1 && parts[0] === 'checkout';
    }

    // Produção: checkout.hotlize.com
    return parts.length > 2 && parts[0] === 'checkout';
  };

  if (isPublicCheckout()) {
    return (
      <Router>
        <Routes>
          <Route path="/:slug" element={<PublicCheckout />} />
          <Route path="*" element={<div className="min-h-screen bg-black flex items-center justify-center text-white">Página não encontrada ou subdomínio inválido.</div>} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/cadastro" element={<Register />} />
        <Route path="/esqueci-senha" element={<ForgotPassword />} />
        <Route path="/redefinir-senha" element={<ResetPassword />} />
        <Route path="/banana/login" element={<AdminLogin />} />
        <Route path="/checkout/:slug" element={<PublicCheckout />} />

        {/* Admin Secret Route - COMPLETELY INDEPENDENT LAYOUT */}
        <Route path="/banana/*" element={
          <ProtectedRoute adminOnly>
            <div className="main-layout future-dark admin-theme">
              <AdminSidebar />
              <WhatsAppButton />
              <div className="content-area">
                <div className="dashboard-container">
                  <Routes>
                    <Route path="/" element={<Admin />} />
                    <Route path="/usuarios" element={<UsersList />} />
                    <Route path="/usuarios/:id" element={<UserDetail />} />
                    <Route path="/bots" element={<BotsList />} />
                    <Route path="/bots/:id" element={<BotDetail />} />
                    <Route path="/assinaturas" element={<SubscriptionsList />} />
                    <Route path="/taxas" element={<FeeConfig />} />
                    <Route path="/transacoes" element={<TransactionsList />} />
                    <Route path="/saques" element={<WithdrawalsList />} />
                    <Route path="/saldo-saque" element={<WithdrawalBalance />} />
                    <Route path="/adquirentes" element={<GatewaysList />} />
                    <Route path="/telegram-apps" element={<TelegramAppsList />} />
                    <Route path="/configuracoes" element={<PlatformSettings />} />
                    <Route path="/usuarios-ativos" element={<ActiveUsers />} />
                  </Routes>
                </div>
              </div>
            </div>
          </ProtectedRoute>
        } />

        {/* Protected User Routes */}
        <Route path="/*" element={
          <ProtectedRoute>
            <div className="main-layout future-dark">
              <WhatsAppButton />
              {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
              {confirmModal && (
                <ConfirmModal
                  isOpen={confirmModal.isOpen}
                  title={confirmModal.title}
                  message={confirmModal.msg}
                  onConfirm={confirmModal.onConfirm}
                  onCancel={() => setConfirmModal(null)}
                />
              )}

              {/* Mobile Header */}
              <div className="mobile-header">
                <button className="mobile-menu-btn" onClick={() => setIsSidebarOpen(true)}>
                  <Menu size={24} color="#fff" />
                </button>
                <div className="mobile-logo">
                  <img src={logoPro} alt="Hotlize" height="32" />
                </div>
                <div style={{ width: 24 }}></div>
              </div>

              {isSidebarOpen && (
                <div className="mobile-sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
              )}

              <Sidebar
                accounts={accounts}
                selectedAccountId={selectedAccountId}
                onSelectAccount={setSelectedAccountId}
                onAddAccount={() => setShowLoginModal(true)}
                onDeleteAccount={handleDeleteAccountClick}
                onEditAccount={() => setShowLoginModal(true)}
                onOpenIDFinder={() => setShowIDFinderModal(true)}
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
              />

              <div className="content-area">
                <div className="dashboard-container">
                  {!selectedAccountId && accounts.length === 0 && (
                    <NoAccountBanner onAddAccount={() => setShowLoginModal(true)} />
                  )}
                  <Routes>
                    <Route element={<FreePlanBlocker />}>
                      <Route path="/" element={
                        <Dashboard
                          selectedAccountId={selectedAccountId}
                          onOpenIDFinder={() => setShowIDFinderModal(true)}
                        />
                      } />
                      <Route path="/bots" element={<Bots accountId={selectedAccountId} accounts={accounts} onSelectAccount={setSelectedAccountId} />} />
                      <Route path="/repasse" element={
                        <Repasse
                          selectedAccountId={selectedAccountId}
                          onOpenIDFinder={() => setShowIDFinderModal(true)}
                        />
                      } />
                      <Route path="/plans" element={<Plans />} />
                      <Route path="/assinar" element={<Assinar />} />
                      <Route path="/groups" element={<Groups accountId={selectedAccountId} />} />
                      <Route path="/financeiro" element={<Financeiro />} />
                      <Route path="/media" element={<MediaLib />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/networking" element={<Networking />} />
                      <Route path="/hot-cloner" element={
                        <HotCloner
                          selectedAccountId={selectedAccountId}
                          onOpenIDFinder={() => setShowIDFinderModal(true)}
                        />
                      } />
                    </Route>
                  </Routes>
                </div>
              </div>
            </div>
          </ProtectedRoute>
        } />
      </Routes>

      {/* Global Modals for authenticated users */}
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onSuccess={handleLoginModalSuccess}
        />
      )}

      {showIDFinderModal && (
        <IDFinderModal
          onClose={() => setShowIDFinderModal(false)}
          accountId={selectedAccountId}
        />
      )}
    </Router>
  );
}

export default App;
